import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/client";
import { PRESET_LABELS } from "../utils/permissions";

const ACTION_LABEL = {
  CREATED: "נוצר", UPDATED: "עודכן", DELETED: "נמחק",
  APPROVED: "אושר", REJECTED: "נדחה", ENDED: "הושלם",
  DRAW: "משיכה", REPAY: "החזר", CREATE: "נוצר", UPDATE: "עודכן", DELETE: "נמחק",
};
const ACTION_COLOR = {
  CREATED: "var(--success)", CREATE: "var(--success)",
  UPDATED: "var(--accent)", UPDATE: "var(--accent)",
  DELETED: "var(--danger)", DELETE: "var(--danger)",
  APPROVED: "var(--success)", REJECTED: "var(--danger)",
  ENDED: "var(--text-secondary)", DRAW: "var(--warning, #f59e0b)", REPAY: "var(--success)",
};

export default function UserManagementPage({ user }) {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [approvalLog, setApprovalLog] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [feedbackMessages, setFeedbackMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ role: "", status: "", permissions: {} });

  const fetchUsers = useCallback(() => {
    apiClient.get(`/auth/users`).then((res) => setAllUsers(res.data)).catch(console.error);
  }, []);

  const fetchApprovalLog = useCallback(() => {
    apiClient.get(`/auth/approval-log`).then((res) => setApprovalLog(res.data)).catch(console.error);
  }, []);

  const fetchRecentActivity = useCallback(() => {
    apiClient.get(`/auth/activity-logs`, { params: { limit: 5, page: 1 } })
      .then((res) => setRecentActivity(res.data.logs || []))
      .catch(console.error);
  }, []);

  const fetchFeedback = useCallback(() => {
    apiClient.get(`/auth/feedback`).then((res) => setFeedbackMessages(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchApprovalLog();
    fetchRecentActivity();
    fetchFeedback();
  }, [fetchUsers, fetchApprovalLog, fetchRecentActivity, fetchFeedback]);

  const handleApprove = async (userId, role) => {
    setLoading(true); setError("");
    try {
      await apiClient.post(`/auth/approve/${userId}`, { role: role || undefined });
      fetchUsers(); fetchApprovalLog(); fetchRecentActivity();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  };

  const handleReject = async (userId) => {
    if (!window.confirm("לדחות את המשתמש?")) return;
    setLoading(true); setError("");
    try {
      await apiClient.post(`/auth/reject/${userId}`);
      fetchUsers(); fetchApprovalLog(); fetchRecentActivity();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  };

  const startEdit = (u) => {
    setEditingId(u._id);
    setEditForm({
      role: u.role,
      status: u.status,
      permissions: {
        canViewLoans: u.permissions?.canViewLoans ?? true,
        canEditLoans: u.permissions?.canEditLoans ?? false,
        canManageUsers: u.permissions?.canManageUsers ?? false,
        canExportData: u.permissions?.canExportData ?? true,
      },
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setLoading(true); setError("");
    try {
      await apiClient.put(`/auth/users/${editingId}`, editForm);
      fetchUsers(); fetchRecentActivity();
      setEditingId(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  };

  const handleRemove = async (userId, userName) => {
    if (!window.confirm(`להסיר את המשתמש "${userName}" לצמיתות?`)) return;
    setLoading(true); setError("");
    try {
      await apiClient.delete(`/auth/users/${userId}`);
      fetchUsers(); fetchRecentActivity();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  };

  const formatDate = (d) => {
    if (!d) return "";
    const x = new Date(d);
    return x.toLocaleDateString("he-IL") + " " + x.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
  };

  const pendingUsers = allUsers.filter((u) => u.status === "PENDING");
  const feedbackTypeLabel = (t) => (t === "feature" ? "בקשת פיצ'ר" : "דיווח באג");

  return (
    <div className="user-management-page page">
      <header className="user-management-header">
        <h1 className="user-management-title">ניהול משתמשים</h1>
        <p className="user-management-subtitle">בקשות הרשמה, הרשאות, משוב ולוג פעילות</p>
        {error && <p className="message-error user-management-error">{error}</p>}
      </header>

      {/* ── Pending approvals ── */}
      <section className="user-management-section">
        <header className="section-header">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
            <h2 className="section-title">בקשות הרשמה ממתינות</h2>
            {pendingUsers.length > 0 && (
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 20, height: 20, borderRadius: "50%",
                background: "var(--danger)", color: "#fff", fontSize: 11, fontWeight: 700,
              }}>
                {pendingUsers.length}
              </span>
            )}
          </div>
          <p className="section-desc">משתמשים שממתינים לאישור מנהל</p>
        </header>
        <div className="section-table-wrap">
          <table className="table-minimal user-management-table">
            <thead>
              <tr>
                <th>שם</th><th>אימייל</th><th>תפקיד מבוקש</th><th>תאריך בקשה</th><th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {pendingUsers.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--text-secondary)", padding: "var(--space-lg)" }}>אין בקשות ממתינות</td></tr>
              )}
              {pendingUsers.map((u) => (
                <tr key={u._id}>
                  <td>{u.firstName} {u.lastName}</td>
                  <td>{u.email}</td>
                  <td>{PRESET_LABELS[u.rolePreset] ?? u.rolePreset}</td>
                  <td>{formatDate(u.createdAt)}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button type="button" className="btn btn-primary" onClick={() => handleApprove(u._id, u.role)} disabled={loading}>אשר</button>
                      <button type="button" className="btn btn-ghost" onClick={() => handleReject(u._id)} disabled={loading}>דחה</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Activity sneak peek ── */}
      <section className="user-management-section">
        <header className="section-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 className="section-title">פעילות אחרונה</h2>
            <p className="section-desc">5 הפעולות האחרונות במערכת</p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate("/activity-log")}
            style={{ fontSize: 13, marginTop: 2 }}
          >
            לוג מלא ←
          </button>
        </header>
        <div className="section-table-wrap">
          <table className="table-minimal user-management-table">
            <thead>
              <tr><th>תאריך</th><th>משתמש</th><th>פעולה</th><th>ישות</th></tr>
            </thead>
            <tbody>
              {recentActivity.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text-secondary)", padding: "var(--space-lg)" }}>אין רשומות</td></tr>
              )}
              {recentActivity.map((log) => (
                <tr key={log._id}>
                  <td>{formatDate(log.createdAt)}</td>
                  <td>
                    <span
                      style={{ color: "var(--accent)", cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => navigate(`/users/${log.userId}`)}
                    >
                      {log.userName}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600,
                      background: (ACTION_COLOR[log.action] || "var(--text-secondary)") + "22",
                      color: ACTION_COLOR[log.action] || "var(--text-secondary)",
                    }}>
                      {ACTION_LABEL[log.action] || log.action}
                    </span>
                  </td>
                  <td>{log.entitySummary || log.entityId || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Approval / rejection history ── */}
      <section className="user-management-section">
        <header className="section-header">
          <h2 className="section-title">לוג אישורים ודחיות</h2>
          <p className="section-desc">היסטוריית אישור ודחיית הרשמות</p>
        </header>
        <div className="section-table-wrap">
          <table className="table-minimal user-management-table">
            <thead>
              <tr><th>תאריך</th><th>מנהל</th><th>משתמש</th><th>פעולה</th><th>תפקיד שהוקצה</th></tr>
            </thead>
            <tbody>
              {approvalLog.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--text-secondary)", padding: "var(--space-lg)" }}>אין רשומות</td></tr>
              )}
              {approvalLog.map((log) => (
                <tr key={log._id}>
                  <td>{formatDate(log.createdAt)}</td>
                  <td>{log.adminName}</td>
                  <td>{log.targetUserName} ({log.targetUserEmail})</td>
                  <td>{log.action === "APPROVED" ? "אושר" : "נדחה"}</td>
                  <td>{log.assignedRole || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── All users ── */}
      <section className="user-management-section">
        <header className="section-header">
          <h2 className="section-title">כל המשתמשים</h2>
          <p className="section-desc">רשימת משתמשים, תפקידים והרשאות</p>
        </header>
        <div className="section-table-wrap">
          <table className="table-minimal user-management-table">
            <thead>
              <tr><th>שם</th><th>אימייל</th><th>תפקיד</th><th>סטטוס</th><th>כניסה אחרונה</th><th>פעולות</th></tr>
            </thead>
            <tbody>
              {allUsers.map((u) => (
                <React.Fragment key={u._id}>
                  <tr>
                    <td>
                      <span
                        style={{ color: "var(--accent)", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => navigate(`/users/${u._id}`)}
                      >
                        {u.firstName} {u.lastName}
                      </span>
                    </td>
                    <td>{u.email}</td>
                    <td>{PRESET_LABELS[u.rolePreset] ?? u.rolePreset}</td>
                    <td>{u.status}</td>
                    <td>{formatDate(u.lastLoginAt)}</td>
                    <td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button type="button" className="btn btn-ghost" onClick={() => navigate(`/users/${u._id}`)}>פרופיל</button>
                        <button type="button" className="btn btn-ghost" onClick={() => startEdit(u)} disabled={loading}>ערוך</button>
                        <button
                          type="button" className="btn btn-ghost"
                          onClick={() => handleRemove(u._id, `${u.firstName} ${u.lastName}`)}
                          disabled={loading || String(u._id) === String(user?.id)}
                          title={String(u._id) === String(user?.id) ? "לא ניתן להסיר את עצמך" : ""}
                        >
                          הסר
                        </button>
                      </div>
                    </td>
                  </tr>
                  {editingId === u._id && (
                    <tr style={{ background: "var(--bg-subtle)" }}>
                      <td colSpan={6} style={{ padding: "var(--space-lg)" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-md)", alignItems: "center" }}>
                          <label className="label" style={{ marginBottom: 0 }}>
                            תפקיד&nbsp;
                            <select className="select" value={editForm.role} onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))} style={{ width: "auto", minWidth: 100 }}>
                              <option value="VIEWER">צופה</option>
                              <option value="EDITOR">עורך</option>
                              <option value="ADMIN">מנהל</option>
                            </select>
                          </label>
                          <label className="label" style={{ marginBottom: 0 }}>
                            סטטוס&nbsp;
                            <select className="select" value={editForm.status} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))} style={{ width: "auto", minWidth: 100 }}>
                              <option value="PENDING">ממתין</option>
                              <option value="ACTIVE">פעיל</option>
                              <option value="REJECTED">נדחה</option>
                            </select>
                          </label>
                          {[
                            { key: "canViewLoans", label: "צפייה בהלוואות" },
                            { key: "canEditLoans", label: "עריכת הלוואות" },
                            { key: "canManageUsers", label: "ניהול משתמשים" },
                            { key: "canExportData", label: "ייצוא נתונים" },
                          ].map(({ key, label }) => (
                            <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                              <input
                                type="checkbox"
                                checked={editForm.permissions?.[key] ?? false}
                                onChange={(e) => setEditForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: e.target.checked } }))}
                              />
                              {label}
                            </label>
                          ))}
                          <button type="button" className="btn btn-primary" onClick={saveEdit} disabled={loading}>שמור</button>
                          <button type="button" className="btn btn-ghost" onClick={() => setEditingId(null)} disabled={loading}>ביטול</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Feedback messages ── */}
      <section className="user-management-section">
        <header className="section-header">
          <h2 className="section-title">הודעות משתמשים (משוב / באגים)</h2>
          <p className="section-desc">משוב ובקשות דיווח באגים שנשלחו מהמערכת</p>
        </header>
        <div className="section-table-wrap">
          <table className="table-minimal user-management-table">
            <thead>
              <tr><th>תאריך</th><th>משתמש</th><th>אימייל</th><th>סוג</th><th>הודעה</th></tr>
            </thead>
            <tbody>
              {feedbackMessages.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--text-secondary)", padding: "var(--space-lg)" }}>אין הודעות</td></tr>
              )}
              {feedbackMessages.map((msg) => (
                <tr key={msg._id}>
                  <td>{formatDate(msg.createdAt)}</td>
                  <td>{msg.userName}</td>
                  <td>{msg.userEmail}</td>
                  <td>{feedbackTypeLabel(msg.type)}</td>
                  <td style={{ maxWidth: 320, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{msg.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
